const _ = require('lodash')
const coreUtils = require('@wix/santa-core-utils')
const effectsUtils = require('../helpers/effectsUtils')
const dataUtils = require('../helpers/dataUtils')
const EFFECTS_LIST_TYPE = 'EffectsList'
const EFFECTS = 'effects'
const REF_ARRAY_TYPE = 'RefArray'
const TRIGGERS_TYPE = 'Triggers'
const TRIGGERS_NAMESPACE = 'triggers'
const REACTIONS_TYPE = 'Reactions'
const REACTIONS_NAMESPACE = 'reactions'
const DESKTOP = 'DESKTOP'
const MOBILE = 'MOBILE'

const {
    createItemWithValues,
    createNamedEffectItem,
    createTimeAnimationItem,
    createPlayReactionItem,
    createTimeAnimationOptionsItem,
    createMobileVariantRelationItem,
    createEmptyTimeAnimationOptionsItem
} = effectsUtils

const getFlattenComponentsWithBehaviors = (structure, isMobile) => {
    return coreUtils.dataUtils.getAllCompsInStructure(structure, isMobile, comp => comp.behaviorQuery)
}

const getAllComponentsWithBehaviors = structure => {
    const flattenDesktopComponents = getFlattenComponentsWithBehaviors(structure, false)
    const flattenMobileComponents = getFlattenComponentsWithBehaviors(structure, true)
    const desktopCompsIds = _.map(flattenDesktopComponents, 'id')
    const mobileCompsIds = _.map(flattenMobileComponents, 'id')
    const allUniqueCompsWithBehavior = _.merge({}, flattenDesktopComponents, flattenMobileComponents)
    const mobileOnlyComponentsIds = _.difference(mobileCompsIds, desktopCompsIds)
    return {
        flattenDesktopComponents,
        flattenMobileComponents,
        allUniqueCompsWithBehavior,
        mobileOnlyComponentsIds,
        mobileCompsIds
    }
}

const addQueriesOnCompStructure = (structure, queriesAndIdsToSet) => {
    if (structure) {
        _.forEach(queriesAndIdsToSet, (key, value) => {
            structure[value] = key
        })
    }
}

const setNamedAndTimeAnimationOptions = (data, behavior, isNamedEffect, uniqueIdGenerator) => {
    let timeAnimationOptionsItem
    if (isNamedEffect) {
        const namedEffectItem = createNamedEffectItem(behavior, uniqueIdGenerator)
        _.set(data, [EFFECTS, namedEffectItem.id], namedEffectItem)

        timeAnimationOptionsItem = createTimeAnimationOptionsItem(behavior, namedEffectItem.id, uniqueIdGenerator)
    } else {
        timeAnimationOptionsItem = createEmptyTimeAnimationOptionsItem(uniqueIdGenerator)
    }
    _.set(data, [EFFECTS, timeAnimationOptionsItem.id], timeAnimationOptionsItem)
    return timeAnimationOptionsItem
}

const setDefaultEffect = (data, compStructure, behavior, isNamedEffect, uniqueIdGenerator) => {
    const timeAnimationOptionsItem = setNamedAndTimeAnimationOptions(data, behavior, isNamedEffect, uniqueIdGenerator)
    const refArrayItem = createItemWithValues(REF_ARRAY_TYPE, EFFECTS, [timeAnimationOptionsItem.id], uniqueIdGenerator)
    _.set(data, [EFFECTS, refArrayItem.id], refArrayItem)

    const timeAnimationItem = createTimeAnimationItem(refArrayItem.id, uniqueIdGenerator)
    _.set(data, [EFFECTS, timeAnimationItem.id], timeAnimationItem)

    const effectsListItem = createItemWithValues(EFFECTS_LIST_TYPE, EFFECTS, [timeAnimationItem.id], uniqueIdGenerator)
    _.set(data, [EFFECTS, effectsListItem.id], effectsListItem)

    return {timeAnimationItem, refArrayItem, effectsListItem}
}

const setEffectInMobileVariant = (
    data,
    behavior,
    isNamedEffect,
    timeAnimationItemId,
    refArrayItem,
    uniqueIdGenerator
) => {
    const timeAnimationOptionsItem = setNamedAndTimeAnimationOptions(data, behavior, isNamedEffect, uniqueIdGenerator)
    const mobileVariantRelationItem = createMobileVariantRelationItem(
        timeAnimationItemId,
        timeAnimationOptionsItem.id,
        uniqueIdGenerator
    )
    _.set(data, [EFFECTS, mobileVariantRelationItem.id], mobileVariantRelationItem)

    const updatedRefArray = dataUtils.updateItemWithValues(refArrayItem, [mobileVariantRelationItem.id])
    _.set(data, [EFFECTS, refArrayItem.id], updatedRefArray)
}

const addViewportEnterVariantAndTrigger = (data, compStructure, uniqueIdGenerator) => {
    const viewportEnterVariantItem = {
        id: uniqueIdGenerator.getUniqueId('variants', '-'),
        componentId: compStructure.id,
        trigger: 'viewport-enter',
        type: 'Trigger',
        params: {
            threshold: 0.15
        }
    }
    _.set(data, ['variants_data', viewportEnterVariantItem.id], viewportEnterVariantItem)

    const {triggersQuery} = compStructure
    let triggerItem
    if (triggersQuery) {
        const updatedTriggersObject = dataUtils.updateItemWithValues(data[TRIGGERS_NAMESPACE][triggersQuery], [
            viewportEnterVariantItem.id
        ])
        _.set(data, [TRIGGERS_NAMESPACE, triggersQuery], updatedTriggersObject)
    } else {
        triggerItem = effectsUtils.createItemWithValues(
            TRIGGERS_TYPE,
            TRIGGERS_NAMESPACE,
            [viewportEnterVariantItem.id],
            uniqueIdGenerator
        )
        _.set(data, [TRIGGERS_NAMESPACE, triggerItem.id], triggerItem)
    }
    return {viewportEnterVariantItem, triggerItem}
}

const addReactionData = (data, compStructure, effectId, variantsIds, uniqueIdGenerator) => {
    const playReactionItem = createPlayReactionItem(effectId, uniqueIdGenerator)
    _.set(data, [REACTIONS_NAMESPACE, playReactionItem.id], playReactionItem)

    const reactionsListItem = createItemWithValues(
        REACTIONS_TYPE,
        REACTIONS_NAMESPACE,
        [playReactionItem.id],
        uniqueIdGenerator
    )
    _.set(data, [REACTIONS_NAMESPACE, reactionsListItem.id], reactionsListItem)

    const variantRelationData = {
        id: uniqueIdGenerator.getUniqueId(REACTIONS_NAMESPACE, '-'),
        variants: variantsIds,
        scopedDataId: reactionsListItem.id,
        from: `#${compStructure.id}`
    }
    const variantRelationItem = dataUtils.variantRelation.create(variantRelationData)
    _.set(data, [REACTIONS_NAMESPACE, variantRelationItem.id], variantRelationItem)

    return variantRelationItem.id
}

const addReaction = (data, compStructure, effectId, variantsIds, uniqueIdGenerator) => {
    const variantRelationId = addReactionData(data, compStructure, effectId, variantsIds, uniqueIdGenerator)
    const {reactionsQuery} = compStructure
    if (reactionsQuery) {
        const updatedRefArray = dataUtils.updateItemWithValues(data[REACTIONS_NAMESPACE][reactionsQuery], [
            variantRelationId
        ])
        _.set(data, [REACTIONS_NAMESPACE, reactionsQuery], updatedRefArray)
        return reactionsQuery
    }
    const reactionsRefArrayItem = createItemWithValues(
        REF_ARRAY_TYPE,
        REACTIONS_NAMESPACE,
        [variantRelationId],
        uniqueIdGenerator
    )
    _.set(data, [REACTIONS_NAMESPACE, reactionsRefArrayItem.id], reactionsRefArrayItem)
    return reactionsRefArrayItem.id
}

const setInDefaultAndMobile = (
    data,
    compStructure,
    behavior,
    isDefaultNamedEffect,
    isMobileNamedEffect,
    uniqueIdGenerator,
    shouldWriteToMobileVariant,
    mobileBehavior
) => {
    const {timeAnimationItem, refArrayItem, effectsListItem} = setDefaultEffect(
        data,
        compStructure,
        behavior,
        isDefaultNamedEffect,
        uniqueIdGenerator
    )

    if (shouldWriteToMobileVariant) {
        const overrideBehavior = mobileBehavior ? mobileBehavior : behavior
        setEffectInMobileVariant(
            data,
            overrideBehavior,
            isMobileNamedEffect,
            timeAnimationItem.id,
            refArrayItem,
            uniqueIdGenerator
        )
    }

    const {viewportEnterVariantItem, triggerItem} = addViewportEnterVariantAndTrigger(
        data,
        compStructure,
        uniqueIdGenerator
    )

    const reactionRefArrayId = addReaction(
        data,
        compStructure,
        timeAnimationItem.id,
        [viewportEnterVariantItem.id],
        uniqueIdGenerator
    )

    const queriesToIdsMap = {
        effectsQuery: effectsListItem.id,
        ...(triggerItem && {triggersQuery: triggerItem.id}),
        ...(reactionRefArrayId && {reactionsQuery: reactionRefArrayId})
    }

    return queriesToIdsMap
}

const deleteBehaviorQuery = compStructure => {
    if (compStructure) {
        delete compStructure.behaviorQuery
    }
}

const migratePage = (structure, data, uniqueIdGenerator) => {
    data.effects = data.effects || {}
    data.variants_data = data.variants_data || {}
    data.triggers = data.triggers || {}
    data.reactions = data.reactions || {}
    const {
        flattenDesktopComponents,
        flattenMobileComponents,
        allUniqueCompsWithBehavior,
        mobileOnlyComponentsIds,
        mobileCompsIds
    } = getAllComponentsWithBehaviors(structure)

    _.forEach(allUniqueCompsWithBehavior, compStructure => {
        const {behaviorQuery} = compStructure
        let behaviorItems, queriesToIdsMap
        try {
            behaviorItems = JSON.parse(data.behaviors_data[behaviorQuery].items)
        } catch (e) {
            const error = {Error: e, behaviorQuery}
            throw error
        }
        const screenInItems = behaviorItems.filter(item => item.action === 'screenIn')

        if (_.isEmpty(screenInItems)) {
            return
        }

        //means that either it is a mobileOnly comp or it has animation in one viewMode only
        if (screenInItems.length === 1) {
            const behavior = screenInItems[0]
            if (behavior.viewMode === MOBILE) {
                queriesToIdsMap = setInDefaultAndMobile(
                    data,
                    compStructure,
                    behavior,
                    false,
                    true,
                    uniqueIdGenerator,
                    true
                )
            } else if (behavior.viewMode === undefined) {
                //if comp is mobileOnly then default should be empty effect and screenIn should be set on the mobile variant
                if (_.includes(mobileOnlyComponentsIds, compStructure.id)) {
                    queriesToIdsMap = setInDefaultAndMobile(
                        data,
                        compStructure,
                        behavior,
                        false,
                        true,
                        uniqueIdGenerator
                    )
                } else {
                    //set same effect on both desktop and mobile variant
                    queriesToIdsMap = setInDefaultAndMobile(
                        data,
                        compStructure,
                        behavior,
                        true,
                        true,
                        uniqueIdGenerator,
                        true
                    )
                }
            } else if (behavior.viewMode === DESKTOP) {
                if (_.includes(mobileCompsIds, compStructure.id)) {
                    //if comp exists on mobile structure also then it is classic editor, and we write screenIn on default and empty on mobile variant
                    queriesToIdsMap = setInDefaultAndMobile(
                        data,
                        compStructure,
                        behavior,
                        true,
                        false,
                        uniqueIdGenerator,
                        true
                    )
                } else {
                    // it is editorX so we write screenIn only on default
                    queriesToIdsMap = setInDefaultAndMobile(
                        data,
                        compStructure,
                        behavior,
                        true,
                        false,
                        uniqueIdGenerator,
                        false
                    )
                }
            }
        } else if (screenInItems.length === 2) {
            const desktopBehavior = _.find(screenInItems, {viewMode: DESKTOP})
            const mobileBehavior = _.find(screenInItems, {viewMode: MOBILE})
            queriesToIdsMap = setInDefaultAndMobile(
                data,
                compStructure,
                desktopBehavior,
                true,
                true,
                uniqueIdGenerator,
                true,
                mobileBehavior
            )
        }

        addQueriesOnCompStructure(flattenDesktopComponents[compStructure.id], queriesToIdsMap)
        addQueriesOnCompStructure(flattenMobileComponents[compStructure.id], queriesToIdsMap)
        const itemsWithoutScreenIn = behaviorItems.filter(item => item.action !== 'screenIn')
        if (_.isEmpty(itemsWithoutScreenIn)) {
            delete data.behaviors_data[compStructure.behaviorQuery]
            deleteBehaviorQuery(flattenDesktopComponents[compStructure.id])
            deleteBehaviorQuery(flattenMobileComponents[compStructure.id])
        } else {
            _.set(data, ['behaviors_data', compStructure.behaviorQuery, 'items'], JSON.stringify(itemsWithoutScreenIn))
        }
    })
}

module.exports = {
    name: 'migrateScreenInBehaviorsToEntranceEffects',
    version: 0,
    experimentalVersions: [
        {version: 1, experiment: 'dm_screenInBehaviorsToEntranceEffectsFixer'},
        {version: 1, experiment: 'dm_screenInBehaviorsToEntranceEffectsFixer_loggedIn'}
    ],
    exec(pageJson, pageIdsArray, magicObject) {
        const {uniqueIdGenerator} = magicObject.dataFixerUtils
        const pageBehaviorsData = pageJson.data.behaviors_data
        const pageEffectsData = pageJson.data.effects

        if (
            (!magicObject.isExperimentOpen('dm_screenInBehaviorsToEntranceEffectsFixer') &&
                !magicObject.isExperimentOpen('dm_screenInBehaviorsToEntranceEffectsFixer_loggedIn')) ||
            _.isEmpty(pageBehaviorsData) ||
            !_.isEmpty(pageEffectsData)
        ) {
            return
        }

        const {structure, data} = pageJson
        migratePage(structure, data, uniqueIdGenerator)
    }
}
